import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const FUNDING_TYPE_ORDER = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C"];
const UPDATE_CATEGORY_OPTIONS = ["New Company", "Updates"];

function MultiSelectField({ label, value, onChange, options }) {
  const renderSelectedValues = (selected) =>
    selected.length > 0 ? selected.join(", ") : "";

  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={renderSelectedValues}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={value.includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      elevation={0}
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "14px !important",
        backgroundColor: "#f8fafc",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.25,
          minHeight: 46,
          "& .MuiAccordionSummary-content": {
            my: 0.75,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1f2937" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
            {subtitle}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function Filters({
  companies,
  selectedIndustry,
  setSelectedIndustry,
  selectedCountry,
  setSelectedCountry,
  selectedStage,
  setSelectedStage,
  selectedUpdateCategory,
  setSelectedUpdateCategory,
  selectedEstimatedRevenueRange,
  setSelectedEstimatedRevenueRange,
  selectedFundingPeriod,
  setSelectedFundingPeriod,
  selectedFundingYear,
  setSelectedFundingYear,
}) {
  const industryOptions = [
    ...new Set(
      companies
        .flatMap((c) => c.BucketIndustries || [])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ];

  const countryOptions = [
    ...new Set(
      companies
        .map((c) => c["Headquarters Location"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => a.localeCompare(b));

  const stageOptions = [
    ...new Set(
      companies
        .map((c) => c["Investment Stage"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => {
    const aIndex = FUNDING_TYPE_ORDER.indexOf(a);
    const bIndex = FUNDING_TYPE_ORDER.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.localeCompare(b);
  });

  const estimatedRevenueRangeOptions = [
    ...new Set(
      companies
        .map((c) => c["Estimated Revenue Range"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => a.localeCompare(b));

  const quarterOptions = ["Q1", "Q2", "Q3", "Q4"].filter((quarter) =>
    companies.some((c) => c["Funding Quarter"] === quarter)
  );

  const monthOrder = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthOptions = monthOrder.filter((month) =>
    companies.some((c) => c["Funding Month"] === month)
  );

  const yearOptions = [
    ...new Set(
      companies
        .map((c) => c["Funding Year"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => Number(b) - Number(a));

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <FilterAccordion
        title="Core Filters"
        subtitle="Industry, geography, stage, and list status"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectField
            label="Industry"
            value={selectedIndustry}
            onChange={setSelectedIndustry}
            options={industryOptions}
          />
          <MultiSelectField
            label="Country"
            value={selectedCountry}
            onChange={setSelectedCountry}
            options={countryOptions}
          />
          <MultiSelectField
            label="Investment Stage"
            value={selectedStage}
            onChange={setSelectedStage}
            options={stageOptions}
          />
          <MultiSelectField
            label="List Status"
            value={selectedUpdateCategory}
            onChange={setSelectedUpdateCategory}
            options={UPDATE_CATEGORY_OPTIONS}
          />
        </Box>
      </FilterAccordion>

      <FilterAccordion
        title="Additional Filters"
        subtitle="Commercial metadata"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectField
            label="Estimated Revenue Range"
            value={selectedEstimatedRevenueRange}
            onChange={setSelectedEstimatedRevenueRange}
            options={estimatedRevenueRangeOptions}
          />
        </Box>
      </FilterAccordion>

      <FilterAccordion
        title="Time Filters"
        subtitle="Funding period and year"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectField
            label="Time Period"
            value={selectedFundingPeriod}
            onChange={setSelectedFundingPeriod}
            options={[...quarterOptions, ...monthOptions]}
          />
          <MultiSelectField
            label="Year"
            value={selectedFundingYear}
            onChange={setSelectedFundingYear}
            options={yearOptions}
          />
        </Box>
      </FilterAccordion>
    </Box>
  );
}
